import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { CONTEXT_MAX_BATCH_SIZE_BYTES, limitBatchSize } from '@/lib/attachments'
import { toast } from 'sonner'
import { getFileIconComponent } from './fileIcons'
import { useUploadContextFiles } from '@/hooks'

type FileUploadItem = {
  id: string
  file: File
  description: string
}

interface FilesUploadDialogProps {
  projectId: string
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function FilesUploadDialog({ projectId, open, onOpenChange }: FilesUploadDialogProps) {
  const [items, setItems] = useState<FileUploadItem[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const uploadFilesMutation = useUploadContextFiles(projectId);

  useEffect(() => {
    if (!open) setItems([])
  }, [open])

  const canUpload = items.length > 0;

  const addFiles = (files: FileList | null) => {
    if (!files) return
    const queuedBytes = items.reduce((sum, item) => sum + item.file.size, 0)
    const { accepted, errors } = limitBatchSize(Array.from(files), CONTEXT_MAX_BATCH_SIZE_BYTES, queuedBytes)
    errors.forEach((message) => toast.error(message))
    setItems((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        id: `${file.name}-${file.lastModified}-${crypto.getRandomValues(new Uint32Array(1))[0]}`,
        file,
        description: '',
      })),
    ])
  }

  const handleUpload = async () => {
    if (!canUpload) return

    const formData = new FormData()
    items.forEach((item) => {
      formData.append('files', item.file)
      formData.append('descriptions', item.description)
    })

    try {
      await uploadFilesMutation.mutateAsync(formData)
      toast.success('Uploaded successfully')
      onOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Upload failed')
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    addFiles(event.dataTransfer.files)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>Upload File(s)</DialogTitle>
        </DialogHeader>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="rounded-lg border border-dashed p-6 text-center text-muted-foreground"
        >
          Drag your file(s) to start uploading /{' '}
          <button
            className="font-bold text-foreground underline cursor-pointer active:opacity-70"
            onClick={() => fileInputRef.current?.click()}
          >
            Browse Files.
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto">
          {items.map((item) => {
            const Icon = getFileIconComponent(item.file.name)
            return (
              <div key={item.id} data-testid="file-row" className="grid grid-cols-[1fr_1fr_36px] items-center gap-3 rounded-lg border p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="rounded border p-2 text-muted-foreground shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm font-medium truncate">{item.file.name}</span>
                      </TooltipTrigger>
                      <TooltipContent>{item.file.name}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  placeholder="Description (Optional)"
                  value={item.description}
                  data-testid="file-description"
                  aria-label={`Description for ${item.file.name}`}
                  onChange={(e) =>
                    setItems((prev) =>
                      prev.map((entry) =>
                        entry.id === item.id ? { ...entry, description: e.target.value } : entry
                      )
                    )
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${item.file.name}`}
                  onClick={() => setItems((prev) => prev.filter((entry) => entry.id !== item.id))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload || uploadFilesMutation.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
